import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ShieldCheck, Plus, Pencil, Trash2, Users, ChevronDown, ChevronRight, ArrowDown, HelpCircle, X, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_CONFIG = {
  1: { label: 'Organization Head', color: 'bg-red-50 border-red-200 text-red-800', badge: 'bg-red-100 text-red-700', arrow: 'text-red-300' },
  2: { label: 'Team Managers', color: 'bg-amber-50 border-amber-200 text-amber-800', badge: 'bg-amber-100 text-amber-700', arrow: 'text-amber-300' },
  3: { label: 'Senior Executives', color: 'bg-blue-50 border-blue-200 text-blue-800', badge: 'bg-blue-100 text-blue-700', arrow: 'text-blue-300' },
  4: { label: 'Executives', color: 'bg-slate-50 border-slate-200 text-slate-800', badge: 'bg-slate-100 text-slate-600', arrow: 'text-slate-300' },
};

const PERM_GROUPS = {
  'Administration': ['manage_users', 'manage_teams', 'manage_roles', 'manage_settings'],
  'Clients': ['manage_companies'],
  'Recruitment': ['manage_positions', 'manage_resumes', 'manage_interviews', 'manage_closures', 'manage_joinings'],
  'Communication': ['manage_announcements'],
  'Performance': ['manage_targets', 'view_own_targets'],
  'Reports': ['view_analytics', 'view_revenue', 'view_activity_log'],
  'Data': ['bulk_upload'],
};

export default function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [hierarchy, setHierarchy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState({ key: '', display_name: '', level: 4, permissions: [], description: '' });
  const [saving, setSaving] = useState(false);
  const [showGuide, setShowGuide] = useState(() => {
    try { return localStorage.getItem('roles_guide_dismissed') !== 'true'; } catch { return true; }
  });

  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const loadAll = async () => {
    try {
      const [rolesRes, permsRes, hierRes] = await Promise.all([
        axios.get(`${API}/roles/definitions`, { headers: getHeaders() }),
        axios.get(`${API}/roles/permissions`, { headers: getHeaders() }),
        axios.get(`${API}/roles/hierarchy`, { headers: getHeaders() })
      ]);
      setRoles(rolesRes.data);
      setAllPerms(permsRes.data);
      setHierarchy(hierRes.data);
    } catch { toast.error('Failed to load roles'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const openCreate = () => {
    setEditingKey(null);
    setForm({ key: '', display_name: '', level: 4, permissions: [], description: '' });
    setDialogOpen(true);
  };

  const openEdit = (role) => {
    setEditingKey(role.key);
    setForm({
      key: role.key,
      display_name: role.display_name,
      level: role.level,
      permissions: role.permissions || [],
      description: role.description || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.display_name) { toast.error('Role name is required'); return; }
    setSaving(true);
    try {
      if (editingKey) {
        await axios.put(`${API}/roles/definitions/${editingKey}`, {
          display_name: form.display_name, permissions: form.permissions, description: form.description
        }, { headers: getHeaders() });
        toast.success('Role updated');
      } else {
        if (!form.key) { toast.error('Role key is required'); setSaving(false); return; }
        await axios.post(`${API}/roles/definitions`, form, { headers: getHeaders() });
        toast.success('Role created');
      }
      setDialogOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (key) => {
    if (!window.confirm('Delete this role? Users assigned to it must be reassigned first.')) return;
    try {
      await axios.delete(`${API}/roles/definitions/${key}`, { headers: getHeaders() });
      toast.success('Role deleted');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const togglePerm = (perm) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm) ? prev.permissions.filter(p => p !== perm) : [...prev.permissions, perm]
    }));
  };

  const toggleGroupPerms = (group) => {
    const groupPerms = PERM_GROUPS[group];
    const allSelected = groupPerms.every(p => form.permissions.includes(p));
    if (allSelected) {
      setForm(prev => ({ ...prev, permissions: prev.permissions.filter(p => !groupPerms.includes(p)) }));
    } else {
      setForm(prev => ({ ...prev, permissions: [...new Set([...prev.permissions, ...groupPerms])] }));
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;

  return (
    <div data-testid="role-management-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Organization Roles
          </h1>
          <p className="text-sm text-slate-600 mt-1">Define your organization hierarchy with up to 4 levels</p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-role-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Role
        </Button>
      </div>

      {/* Inline Quick Guide */}
      {showGuide && (
        <div className="mb-6 border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 relative" data-testid="roles-quick-guide">
          <button onClick={() => { setShowGuide(false); localStorage.setItem('roles_guide_dismissed', 'true'); }}
            className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors" data-testid="dismiss-guide-btn">
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-3 mb-3">
            <div className="bg-blue-100 rounded-lg p-2 shrink-0"><Lightbulb className="h-5 w-5 text-blue-600" /></div>
            <div>
              <h3 className="text-sm font-bold text-blue-900">Quick Guide: Setting Up Your Organization Hierarchy</h3>
              <p className="text-xs text-slate-500 mt-0.5">Dismiss this panel anytime. Full guide available under Administration &gt; How to Use.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-0 md:ml-12">
            <div className="bg-white/70 rounded-lg p-3 border border-blue-100">
              <p className="text-xs font-bold text-blue-800 mb-1.5">Understanding the 4 Levels</p>
              <ul className="space-y-1 text-xs text-slate-600">
                <li><span className="font-semibold text-red-700">L1 &mdash; Organization Head:</span> Full platform access, manages users, roles &amp; settings.</li>
                <li><span className="font-semibold text-amber-700">L2 &mdash; Team Managers:</span> Leads a team, views team analytics &amp; targets.</li>
                <li><span className="font-semibold text-blue-700">L3 &mdash; Senior Executives:</span> Experienced recruiters with expanded permissions.</li>
                <li><span className="font-semibold text-slate-700">L4 &mdash; Executives:</span> Day-to-day recruitment work (resumes, interviews).</li>
              </ul>
            </div>
            <div className="bg-white/70 rounded-lg p-3 border border-blue-100">
              <p className="text-xs font-bold text-blue-800 mb-1.5">How to Customize</p>
              <ul className="space-y-1 text-xs text-slate-600">
                <li><span className="font-semibold">Rename roles:</span> Click the pencil icon on any role to change its display name.</li>
                <li><span className="font-semibold">Add custom roles:</span> Click "+ Add Role", pick a level, assign permissions.</li>
                <li><span className="font-semibold">Adjust permissions:</span> Edit a role and toggle permission groups on/off.</li>
                <li><span className="font-semibold">Delete roles:</span> Only custom (non-system) roles can be removed.</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {!showGuide && (
        <button onClick={() => { setShowGuide(true); localStorage.removeItem('roles_guide_dismissed'); }}
          className="mb-4 flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 transition-colors" data-testid="show-guide-btn">
          <HelpCircle className="h-3.5 w-3.5" /> Show setup guide
        </button>
      )}

      {/* Hierarchy Visualization */}
      {hierarchy && (
        <div className="space-y-3 mb-8" data-testid="hierarchy-view">
          {hierarchy.levels.map((level, li) => {
            const cfg = LEVEL_CONFIG[level.level] || LEVEL_CONFIG[4];
            return (
              <React.Fragment key={level.level}>
                {li > 0 && (
                  <div className="flex justify-center">
                    <ArrowDown className={`h-5 w-5 ${LEVEL_CONFIG[level.level]?.arrow || 'text-slate-300'}`} />
                  </div>
                )}
                <div className={`border rounded-xl p-4 ${cfg.color}`} data-testid={`hierarchy-level-${level.level}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className={`text-xs font-bold ${cfg.badge}`}>Level {level.level}</Badge>
                    <span className="text-sm font-bold">{cfg.label}</span>
                  </div>
                  {level.roles.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {level.roles.map(role => (
                        <div key={role.key} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-200 shadow-sm">
                          <ShieldCheck className="h-4 w-4 text-blue-500" />
                          <span className="text-sm font-semibold text-slate-800">{role.display_name}</span>
                          <Badge variant="outline" className="text-xs ml-1"><Users className="h-3 w-3 mr-1" />{role.user_count}</Badge>
                          {role.is_system && <Badge className="bg-slate-100 text-slate-500 text-[10px]">System</Badge>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 italic">No roles at this level</p>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Roles Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
          <p className="text-sm font-bold text-slate-700">All Roles ({roles.length})</p>
        </div>
        <div className="divide-y divide-slate-100">
          {roles.map(role => {
            const cfg = LEVEL_CONFIG[role.level] || LEVEL_CONFIG[4];
            return (
              <div key={role.key} className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors" data-testid={`role-row-${role.key}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <ShieldCheck className="h-5 w-5 text-blue-500 shrink-0" />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{role.display_name}</span>
                      <Badge className={`text-[10px] ${cfg.badge}`}>L{role.level}</Badge>
                      {role.is_system && <Badge className="bg-slate-100 text-slate-500 text-[10px]">System</Badge>}
                    </div>
                    <p className="text-xs text-slate-500">{role.description || `Key: ${role.key}`} &middot; {role.permissions?.length || 0} permissions</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(role)} data-testid={`edit-role-${role.key}`}>
                    <Pencil className="h-4 w-4 text-slate-400" />
                  </Button>
                  {!role.is_system && (
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(role.key)} data-testid={`delete-role-${role.key}`}>
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-900">{editingKey ? 'Edit Role' : 'Create New Role'}</DialogTitle>
            <DialogDescription>{editingKey ? 'Update role name, permissions, and description.' : 'Define a new role for your organization.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Role Name */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Role Name</Label>
              <Input data-testid="role-name-input" value={form.display_name} onChange={e => setForm(p => ({ ...p, display_name: e.target.value }))} placeholder="e.g. Branch Manager" className="mt-1.5 border-blue-200" />
            </div>

            {/* Key (only for create) */}
            {!editingKey && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Role Key <span className="text-slate-400 font-normal">(unique ID, lowercase, no spaces)</span></Label>
                <Input data-testid="role-key-input" value={form.key} onChange={e => setForm(p => ({ ...p, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))} placeholder="e.g. branch_manager" className="mt-1.5 border-blue-200 font-mono text-sm" />
              </div>
            )}

            {/* Level */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Hierarchy Level</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {[1, 2, 3, 4].map(lvl => {
                  const cfg = LEVEL_CONFIG[lvl];
                  return (
                    <button key={lvl} type="button" onClick={() => setForm(p => ({ ...p, level: lvl }))}
                      data-testid={`level-${lvl}-btn`}
                      className={`p-2.5 rounded-lg border text-left transition-all ${form.level === lvl ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-slate-200 hover:border-slate-300'}`}>
                      <Badge className={`text-[10px] mb-1 ${cfg.badge}`}>Level {lvl}</Badge>
                      <p className="text-xs font-medium text-slate-700">{cfg.label}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Description */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Description</Label>
              <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="What this role does" className="mt-1.5 border-blue-200" />
            </div>

            {/* Permissions by Group */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-2 block">Permissions</Label>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {Object.entries(PERM_GROUPS).map(([group, perms]) => {
                  const allSelected = perms.every(p => form.permissions.includes(p));
                  const someSelected = perms.some(p => form.permissions.includes(p));
                  return (
                    <div key={group} className="bg-slate-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Checkbox checked={allSelected} onCheckedChange={() => toggleGroupPerms(group)}
                          className={someSelected && !allSelected ? 'data-[state=unchecked]:bg-blue-100' : ''} />
                        <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">{group}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5 ml-6">
                        {perms.map(perm => {
                          const permDef = allPerms.find(p => p.key === perm);
                          return (
                            <label key={perm} className="flex items-center gap-2 cursor-pointer">
                              <Checkbox checked={form.permissions.includes(perm)} onCheckedChange={() => togglePerm(perm)} />
                              <span className="text-xs text-slate-600">{permDef?.label || perm}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 mt-2">{form.permissions.length} permissions selected</p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? 'Saving...' : editingKey ? 'Update Role' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
